import { useState } from "react";
import { toast } from "sonner";
import { Printer, X, Send } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PRINT_JOB_PO_STATUSES, PRINT_JOB_PO_TERMINAL } from "@/lib/statusMap";
import {
  Select, SelectTrigger, SelectValue,
  SelectContent, SelectItem,
} from "@/components/ui/select";

/**
 * Phase 20.2 — Print Job Process-Owner multi-status modal.
 *
 * - Dropdown of 8 statuses (keys match backend workflow.PRINT_JOB_PO_STATUSES).
 * - Optional remark textarea.
 * - Submit → `POST /api/requisitions/{ref}/action` with
 *   `{action: "print_po_update", po_status, remark}`.
 * - Only `work_done` is terminal; other 7 keep the record at
 *   `print_fulfillment` so the PO can flip again.
 */
export default function PrintPOActionModal({
  open, onClose, requisition, currentUser, onDone, actingAsBanner,
}) {
  const [status, setStatus] = useState(
    (requisition?.form_data || {}).print_po_status || "work_done",
  );
  const [remark, setRemark] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;

  const submit = async () => {
    if (!status || !PRINT_JOB_PO_STATUSES[status]) {
      toast.error("Please choose a status."); return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(
        `/requisitions/${requisition.ref_code}/action`,
        { action: "print_po_update", po_status: status, remark },
      );
      const isTerminal = status === PRINT_JOB_PO_TERMINAL;
      toast.success(isTerminal
        ? "Marked as Work Done — applicant notified. Record locked."
        : "Status updated — applicant notified.");
      onDone?.(data);
      onClose?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#1f2320]/40 backdrop-blur-sm
                    flex items-center justify-center p-4 pj-po-modal-scope"
         data-testid="print-po-modal">
      <div className="bg-white rounded-lg shadow-xl max-w-xl w-full">
        <div className="flex items-center gap-3 px-6 py-4 border-b border-[#eadfc8]">
          <Printer size={20} className="text-[#7a1f2b]" />
          <div>
            <div className="text-[11px] uppercase tracking-widest text-[#2d6b6b] font-semibold">
              Print Fulfilment · {requisition.ref_code}
            </div>
            <div className="font-serif text-lg text-[#1f2320]">
              Update Print PO status
            </div>
          </div>
          <button className="ml-auto text-[#6e7770] hover:text-[#1f2320]"
                  onClick={onClose} aria-label="Close"
                  data-testid="print-po-modal-close">
            <X size={18} />
          </button>
        </div>
        <div className="p-6 space-y-4">
          {actingAsBanner && (
            <div className="text-xs text-[#7a1f2b] bg-[#faf5eb] border border-[#b48a45]
                            rounded px-3 py-2 flex items-start gap-2"
                 data-testid="super-admin-acting-as-banner">
              <strong>Acting as:</strong>&nbsp;{actingAsBanner}
            </div>
          )}
          <div className="block">
            <span className="text-[11px] uppercase tracking-widest text-[#2d6b6b] font-semibold">
              Update Status
            </span>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger
                className="pj-combobox-trigger mt-1 w-full flex items-center justify-between rounded-md border border-[#e2d7c3] bg-white text-[#1f2320] focus:outline-none focus:border-[#7a1f2b] focus:ring-2 focus:ring-[#7a1f2b]/20 transition-colors"
                style={{ fontSize: "16px", lineHeight: "24px",
                          minHeight: "44px", padding: "10px 12px" }}
                data-testid="print-po-status-select">
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent className="pj-combobox-content">
                {Object.entries(PRINT_JOB_PO_STATUSES).map(([k, label]) => (
                  <SelectItem key={k} value={k}
                              className="pj-combobox-item cursor-pointer rounded-sm select-none focus:bg-[#f4e9eb] focus:text-[#7a1f2b] data-[highlighted]:bg-[#f4e9eb]"
                              style={{ fontSize: "16px", lineHeight: "24px",
                                        padding: "8px 10px", minHeight: "40px" }}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="block text-[11px] text-[#6e7770] italic mt-1">
              Only <strong>Work Done</strong> locks the record and marks it
              completed. Other statuses are sticky — you can keep flipping
              until Work Done.
            </span>
          </div>
          <label className="block">
            <span className="text-[11px] uppercase tracking-widest text-[#2d6b6b] font-semibold">
              Remark (optional)
            </span>
            <textarea className="input-vgws mt-1 text-sm" rows={3}
                      value={remark}
                      onChange={(e) => setRemark(e.target.value)}
                      placeholder="Optional free-text note sent in the email to the applicant"
                      data-testid="print-po-remark" />
          </label>
          <div className="text-[11px] text-[#6e7770] italic">
            An email will be dispatched to the applicant{" "}
            {(requisition.form_data || {}).staffEmail
              ? <>(<strong>{requisition.form_data.staffEmail}</strong>)</>
              : null} with the new status.
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-[#eadfc8]">
          <button className="btn-ghost" onClick={onClose} disabled={submitting}
                  data-testid="print-po-cancel">
            Cancel
          </button>
          <button className="btn-vgws btn-teal" onClick={submit}
                  disabled={submitting}
                  data-testid="print-po-submit">
            <Send size={16} /> {submitting ? "Updating…" : "Update Status"}
          </button>
        </div>
      </div>
    </div>
  );
}
